import React from 'react';
import { PaymentElement, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { FiCreditCard } from 'react-icons/fi';
import logger from '../../../utils/logging/logger';
import { usePaymentApi, useGuestPayment } from './paymentApiContext';
import styles from '../PaymentForm.module.css';

// Presentational: the two-stage payment UI — Stage 1 'Proceed to Payment' /
// 'Complete Enrollment' button, and Stage 2 the Stripe form (ExpressCheckout +
// PaymentElement + 'Pay $X' submit). Extracted VERBATIM from PaymentForm's
// render; the inline ExpressCheckout onConfirm money handler is unchanged and
// uses the setError/setProcessing/navigate props + api/logger/stripe/elements.
export default function PaymentSection({
  clientSecret, courses, creatingPaymentIntent, elements,
  handleProceedToPayment, handleSubmit, navigate, paymentIntentCreated,
  processing, productIds, setError, setProcessing,
  setShowCardDivider, setSuccess, showCardDivider, showPaymentForm,
  stripe, success, totalPrice,
}) {
  const paymentApi = usePaymentApi(); // signed-in api, or the guest adapter for new students
  const guest = useGuestPayment(); // null for signed-in students
  return (
    <>
        {!showPaymentForm ? (
          <button
            className={styles.submitButton}
            type="button"
            onClick={handleProceedToPayment}
            disabled={creatingPaymentIntent || processing || !courses || courses.length === 0}
          >
            {creatingPaymentIntent ? (
              <>{totalPrice === 0 ? 'Completing enrollment...' : 'Creating secure payment...'}</>
            ) : (
              <>
                <FiCreditCard size={18} />
                {totalPrice === 0 ? 'Complete Enrollment' : 'Proceed to Payment'}
              </>
            )}
          </button>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            {/* FIX #9: Disable submit button during processing to prevent duplicate payments */}
            {/* Payment Card */}
            <div className={styles.paymentCard}>
              <div className={styles.paymentSectionTitle}>
                <FiCreditCard size={16} />
                Payment information
              </div>
              <div className={styles.cardElementContainer}>
            
                {/* Express Checkout (Apple Pay, Google Pay, Link) */}
                {/* FIX #25: Payment method validation handled by Stripe Elements */}
                {paymentIntentCreated && clientSecret && (
                  <div className={styles.expressCheckoutWrapper}>
                    <ExpressCheckoutElement
                        key={(clientSecret.match(/^(pi_[^_]+)/) || [clientSecret])[0]}
                        options={{
                          buttonType: {
                            applePay: 'check-out',
                            googlePay: 'checkout',
                            paypal: 'pay',
                            // FIX: Removed 'link' from buttonType - not a recognized parameter
                          },
                          buttonHeight: 48,
                          buttonTheme: {
                            applePay: 'black',
                            googlePay: 'black',
                            paypal: 'black',
                            // FIX: Removed 'link' from buttonTheme - not a recognized parameter
                          },
                          layout: {
                            overflow: 'never'
                          },
                          // FIX: Replaced legacy 'wallets' with 'paymentMethods' as recommended by Stripe
                          paymentMethods: {
                            applePay: 'auto',
                            googlePay: 'auto',
                            paypal: 'auto',
                            link: 'auto'
                          }
                        }}
                        onReady={(event) => {
                          const availablePaymentMethods = event?.availablePaymentMethods || {};
                          setShowCardDivider(Boolean(availablePaymentMethods.applePay || availablePaymentMethods.googlePay));
                          console.log('✅ EXPRESS CHECKOUT READY - Full Event:', event);
                          console.log('✅ Event Keys:', Object.keys(event || {}));
                          console.log('✅ availablePaymentMethods:', event?.availablePaymentMethods);
                          logger.debug('Express checkout ready', {
                            availablePaymentMethods: event?.availablePaymentMethods,
                            eventKeys: Object.keys(event || {}),
                            event
                          });
                        }}
                        onError={(event) => {
                          logger.error('Express checkout error', { error: event?.error?.message || event });
                          setError(event?.error?.message || 'Link or wallet payment failed. Please try another method.');
                        }}
                        onConfirm={async (event) => {
                        try {

                          setProcessing(true);
                          setError(null);

                          logger.debug('Express checkout onConfirm triggered', { 
                            event,
                            eventKeys: event ? Object.keys(event) : [],
                            productIds,
                            hasToken: !!localStorage.getItem('faithinsight_token'),
                            hasStripe: !!stripe,
                            hasElements: !!elements
                          });

                          // For Express Checkout, we need to explicitly confirm the payment with Stripe
                          // The onConfirm callback fires BEFORE Stripe processes the payment
                          
                          
                          // Confirm the payment with Stripe
                          const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
                            elements,
                            clientSecret,
                            redirect: 'if_required', // Don't redirect for Express Checkout
                            confirmParams: {
                              return_url: guest?.returnUrl || (window.location.origin + '/portal/confirmation')
                            }
                          });
                          
                          
                          if (confirmError) {
                            logger.error('Stripe confirmPayment error', { 
                              error: confirmError,
                              code: confirmError.code,
                              message: confirmError.message
                            });
                            throw new Error(confirmError.message || 'Payment confirmation failed');
                          }
                          
                          if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                            logger.error('Payment not succeeded after Stripe confirmation', {
                              status: paymentIntent?.status
                            });
                            throw new Error(`Payment failed with status: ${paymentIntent?.status || 'unknown'}`);
                          }
                          
                          
                          // Extract payment intent ID for backend confirmation
                          const paymentIntentId = paymentIntent.id;
                          
                          logger.debug('Payment confirmed with Stripe, proceeding to backend', { 
                            paymentIntentId,
                            status: paymentIntent.status
                          });


                          // Now confirm payment on backend (API service handles token refresh automatically)
                          let confirmationResponse;
                          if (productIds) {
                            confirmationResponse = await paymentApi.confirmProductPayment({
                              paymentIntentId: paymentIntentId
                            });
                          } else {
                            confirmationResponse = await paymentApi.confirmPayment({
                              paymentIntentId: paymentIntentId
                            });
                          }

                          
                          logger.debug('Express checkout confirmation successful', { confirmationResponse });

                          setSuccess(true);
                          setProcessing(false);
                          
                          // Navigate to confirmation page
                          navigate('/confirmation', {
                            state: { purchaseData: confirmationResponse }
                          });
                        } catch (error) {
                          
                          logger.error('Express checkout error', { 
                            error, 
                            errorMessage: error?.message,
                            errorStatus: error?.status,
                            csrfTokenMissing: error?.csrfTokenMissing,
                            isNetworkError: error?.isNetworkError
                          });
                          
                          // Provide specific error messages
                          let errorMsg = 'Payment failed. Please try again.';
                          if (error?.status === 401) {
                            errorMsg = 'Your session has expired. Please refresh the page and try again.';
                          } else if (error?.csrfTokenMissing) {
                            errorMsg = 'Security token missing. Please refresh the page and try again.';
                          } else if (error?.isNetworkError || error?.isTimeout) {
                            errorMsg = 'Network error. Please check your connection and try again.';
                          } else if (error?.data?.message) {
                            errorMsg = error.data.message;
                          } else if (error?.message) {
                            errorMsg = error.message;
                          }
                          
                          
                          setError(errorMsg);
                          setProcessing(false);
                        }
                      }}
                      />
                    {showCardDivider && <div className={styles.orDivider}>or pay with card</div>}
                  </div>
                )}
                
                {paymentIntentCreated && clientSecret ? (
                  <>
                    {/* FIX #4: PaymentElement layout set to accordion (matches comment intent) */}
                    <PaymentElement options={{ layout: 'accordion' }} />
                  </>
                ) : (
                  <div style={{ padding: '0.5rem 0', color: 'var(--fi-muted)', fontSize: '0.9rem' }}>
                    Preparing secure payment...
                  </div>
                )}
              </div>
            </div>

            {/* FIX #9: Disable submit button during processing or after success to prevent duplicate payments */}
            {/* FIX #24: Add ARIA labels for accessibility */}
            <button 
              className={styles.submitButton}
              type="submit" 
              disabled={!stripe || processing || success || !clientSecret}
              aria-label={processing 
                ? (paymentIntentCreated ? 'Processing payment' : 'Preparing payment') 
                : (paymentIntentCreated ? `Pay $${(totalPrice || 0).toFixed(2)}` : 'Continue to payment')
              }
              aria-busy={processing}
            >
              {processing ? (
                <>
                  <div className={styles.loadingSpinner} />
                  {paymentIntentCreated ? 'Processing Payment...' : 'Preparing Payment...'}
                </>
              ) : (
                <>
                  <FiCreditCard size={18} />
                  {/* FIX #9: Use memoized total price instead of calling function in render */}
                  {paymentIntentCreated ? `Pay $${(totalPrice || 0).toFixed(2)}` : `Continue to Payment`}
                </>
              )}
            </button>
          </form>
        )}
    </>
  );
}
